import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Paper,
  Stack,
  Typography,
  Avatar,
  Alert,
  Skeleton,
  Button,
  Tabs,
  Tab,
  Chip,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Checkbox,
  Tooltip,
  Divider,
} from "@mui/material";
import SecurityIcon from "@mui/icons-material/Security";
import SaveIcon from "@mui/icons-material/Save";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import Swal from "sweetalert2";
import { api } from "../../utils/api";

export default function Permissions() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const [roles, setRoles] = useState([]);
  const [grouped, setGrouped] = useState({});
  const [grants, setGrants] = useState({});
  const [activeRole, setActiveRole] = useState("");

  const [dirty, setDirty] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/permissions/list.php");
      setRoles(res.roles || []);
      setGrouped(res.grouped || {});
      setGrants(res.grants || {});
      const first = (res.roles || []).find((r) => r !== "Admin") || "";
      setActiveRole(first);
    } catch (e) {
      setMsg({
        type: "error",
        text: e.message || "Failed to load permissions",
      });
    } finally {
      setLoading(false);
      setDirty(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const isAdmin = activeRole === "Admin";

  const grantSet = useMemo(
    () => new Set(grants[activeRole] || []),
    [grants, activeRole],
  );

  const toggleOne = (key) => {
    if (isAdmin) return;
    const next = new Set(grants[activeRole] || []);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setGrants({ ...grants, [activeRole]: Array.from(next) });
    setDirty(true);
  };

  const toggleModule = (module, allKeys) => {
    if (isAdmin) return;
    const current = new Set(grants[activeRole] || []);
    const everyHas = allKeys.every((k) => current.has(k));
    if (everyHas) {
      allKeys.forEach((k) => current.delete(k));
    } else {
      allKeys.forEach((k) => current.add(k));
    }
    setGrants({ ...grants, [activeRole]: Array.from(current) });
    setDirty(true);
  };

  const save = async () => {
    if (isAdmin) return;
    setSaving(true);
    try {
      await api.post("/permissions/save.php", {
        role: activeRole,
        perm_keys: grants[activeRole] || [],
      });
      setDirty(false);
      Swal.fire({
        icon: "success",
        title: "Saved",
        text: `Permissions updated for ${activeRole}`,
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (e) {
      Swal.fire({ icon: "error", title: "Save Failed", text: e.message });
    } finally {
      setSaving(false);
    }
  };

  const handleRoleChange = async (_, next) => {
    if (dirty) {
      const confirm = await Swal.fire({
        icon: "warning",
        title: "Unsaved changes",
        text: "Switch role without saving? Changes will be lost.",
        showCancelButton: true,
        confirmButtonText: "Discard",
        cancelButtonText: "Stay",
      });
      if (!confirm.isConfirmed) return;
      load();
    }
    setActiveRole(next);
  };

  return (
    <Box>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ sm: "center" }}
        justifyContent="space-between"
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          <Avatar sx={{ bgcolor: "primary.main", width: 48, height: 48 }}>
            <SecurityIcon />
          </Avatar>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 800 }}>
              Permissions
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage granular module access per role
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" spacing={1}>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={save}
            disabled={!dirty || saving || isAdmin}
          >
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </Stack>
      </Stack>

      {msg && (
        <Alert severity={msg.type} sx={{ mb: 2 }} onClose={() => setMsg(null)}>
          {msg.text}
        </Alert>
      )}

      {loading ? (
        <Paper sx={{ p: 3 }}>
          <Skeleton height={48} />
          <Skeleton height={36} />
          <Skeleton height={36} />
          <Skeleton height={36} />
        </Paper>
      ) : (
        <Paper>
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Tabs
              value={activeRole}
              onChange={handleRoleChange}
              variant="scrollable"
              scrollButtons="auto"
            >
              {roles.map((r) => (
                <Tab
                  key={r}
                  value={r}
                  label={
                    <Stack direction="row" spacing={1} alignItems="center">
                      <span>{r}</span>
                      {r === "Admin" && (
                        <AdminPanelSettingsIcon
                          fontSize="small"
                          color="primary"
                        />
                      )}
                      <Chip
                        size="small"
                        label={(grants[r] || []).length}
                        color="info"
                        variant="outlined"
                      />
                    </Stack>
                  }
                />
              ))}
            </Tabs>
          </Box>

          {isAdmin && (
            <Alert severity="info" sx={{ m: 2 }}>
              Admin role has full access to all permissions and cannot be
              modified.
            </Alert>
          )}

          <Box sx={{ overflowX: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Permission</TableCell>
                  <TableCell
                    align="center"
                    sx={{ fontWeight: 700, width: 120 }}
                  >
                    Granted
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.entries(grouped).map(([module, items]) => {
                  const keys = items.map((i) => i.perm_key);
                  const allGranted =
                    isAdmin || keys.every((k) => grantSet.has(k));
                  const someGranted =
                    !allGranted && keys.some((k) => grantSet.has(k));

                  return (
                    <React.Fragment key={module}>
                      <TableRow sx={{ bgcolor: "action.hover" }}>
                        <TableCell>
                          <Typography
                            variant="overline"
                            sx={{ fontWeight: 800, color: "primary.main" }}
                          >
                            {module}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip
                            title={
                              isAdmin
                                ? "Admin always granted"
                                : allGranted
                                  ? "Unselect all in module"
                                  : "Select all in module"
                            }
                          >
                            <span>
                              <Checkbox
                                checked={allGranted}
                                indeterminate={someGranted}
                                disabled={isAdmin}
                                onChange={() => toggleModule(module, keys)}
                              />
                            </span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>

                      {items.map((p) => {
                        const checked = isAdmin || grantSet.has(p.perm_key);
                        return (
                          <TableRow key={p.perm_key} hover>
                            <TableCell>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 600 }}
                              >
                                {p.label}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Checkbox
                                checked={checked}
                                disabled={isAdmin}
                                onChange={() => toggleOne(p.perm_key)}
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </Box>

          <Divider />
          <Stack
            direction="row"
            justifyContent="flex-end"
            spacing={1}
            sx={{ p: 2 }}
          >
            {dirty && (
              <Chip
                label="Unsaved changes"
                color="warning"
                size="small"
                variant="outlined"
              />
            )}
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={save}
              disabled={!dirty || saving || isAdmin}
            >
              {saving ? "Saving..." : "Save Changes"}
            </Button>
          </Stack>
        </Paper>
      )}
    </Box>
  );
}
